import React from "react";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import InsertDriveFileIcon from "@material-ui/icons/InsertDriveFile";
import ImageIcon from "@material-ui/icons/Image";
import PictureAsPdfIcon from "@material-ui/icons/PictureAsPdf";
import CloseButton from "@material-ui/icons/Close";
import { Badge, IconButton, Menu, MenuItem, Tooltip, withStyles } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { getEditMesssageItemSelector } from "redux/selectors/cockpitSelectors";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import cockpitActions from "../redux/actions/cockpitActions";
import { errorMsg } from "redux/reducers/snackMsgsReducers";
import i18n from "i18n";

import { getACleanURI, invalidFileExtensions, traceError } from "../helpers/utilities";

const useStyles = makeStyles(theme => ({
	chip: {
		display: "inline-flex",
		alignItems: "center",
		backgroundColor: theme.palette.grey[100],
		border: `1px solid ${theme.palette.divider}`,
		borderRadius: 8,
		padding: "4px 4px 4px 8px",
		gap: 6,
		margin: 5,
		width: "calc(20% - 10px)",
		minWidth: 0,
		transition: "background-color 0.2s ease, box-shadow 0.2s ease",
		"&:hover": {
			backgroundColor: theme.palette.grey[200],
			boxShadow: "0 2px 8px rgba(0,0,0,0.1)"
		}
	},
	fileIcon: {
		fontSize: 18,
		flexShrink: 0
	},
	textContent: {
		flex: 1,
		minWidth: 0,
		overflow: "hidden",
		cursor: "pointer"
	},
	fileName: {
		display: "block",
		overflow: "hidden",
		textOverflow: "ellipsis",
		whiteSpace: "nowrap",
		fontSize: 12,
		fontWeight: 600,
		lineHeight: 1.4,
		color: theme.palette.text.primary
	},
	fileSize: {
		display: "block",
		fontSize: 10,
		fontWeight: 700,
		color: theme.palette.primary.main,
		lineHeight: 1.2
	},
	closeBtn: {
		padding: 2,
		flexShrink: 0
	},
	closeIcon: {
		fontSize: 14
	}
}));

const StyledBadge = withStyles(theme => ({
	badge: {
		top: 0,
		border: `2px solid ${theme.palette.background.paper}`,
		padding: "0 4px",
		backgroundColor: "#545454",
		color: "white"
	}
}))(Badge);

const getFileIcon = ext => {
	if (["jpg", "jpeg", "png", "gif", "svg", "webp", "bmp"].includes(ext)) return ImageIcon;
	if (ext === "pdf") return PictureAsPdfIcon;
	return InsertDriveFileIcon;
};

const getFileIconColor = ext => {
	if (["jpg", "jpeg", "png", "gif", "svg", "webp", "bmp"].includes(ext)) return "#000000";
	if (ext === "pdf") return "#e53935";
	return "#1e88e5";
};

export const Attachment = propsAtt => {
	let { selectedConversation, attachments, selectedAssistantID } = useSelector(
		getEditMesssageItemSelector,
		shallowEqual
	);
	const dispatch = useDispatch();
	const classes = useStyles();

	const openAttachment = async propsAtt => {
		const { fileName, mimeType } = propsAtt;

		const file = attachments.length > 0 && attachments.filter(e => e.name === fileName)[0].file;
		let url = getFileUrl(mimeType, file);
		if (url) {
			try {
				window.open(url);
			} catch (e) {
				traceError(`openAttachment-${url} failed: ${e.message}`);
				// console.error("error", e);
			}
		} else if (propsAtt.url) {
			try {
				window.open(getACleanURI(propsAtt.url));
			} catch (e) {
				traceError(`openAttachment-${propsAtt.url} (props) failed: ${e.message}`);
				// console.error("error", e);
			}
		}
	};

	const formatBytes = (a, b = 2) => {
		if (0 === a) return "0 Bytes";
		const c = 0 > b ? 0 : b;
		const d = Math.floor(Math.log(a) / Math.log(1024));
		return (
			parseFloat((a / Math.pow(1024, d)).toFixed(c)) +
			" " +
			["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"][d]
		);
	};

	function getFileUrl(type, file) {
		let url;
		const fileExtension = file?.name.split(".").pop();
		let typeExist = type !== "" ? type : fileExtension;
		if (typeExist && file) {
			try {
				if (invalidFileExtensions.includes(fileExtension)) {
					const msg = `${file.name} ${i18n.t("COC.noTsupportedPreview")}`;
					dispatch(errorMsg(msg));
					traceError(`getFileUrl-${msg}`);
				} else {
					url = (window.URL || window.webkitURL).createObjectURL(file);
				}
			} catch (err) {
				traceError(`getFileUrl-${err.message}`);
			}
		}
		return url;
	}

	const deleteAttachment = (index, fileName) => {
		dispatch(
			cockpitActions.deleteFile(index, fileName, selectedConversation?._id, selectedAssistantID)
		);
	};

	const ext = propsAtt.fileName?.split(".").pop()?.toLowerCase();
	const FileIcon = getFileIcon(ext);
	const iconColor = getFileIconColor(ext);

	return (
		<Tooltip title={propsAtt.fileName} placement="top">
			<span className={classes.chip}>
				<FileIcon className={classes.fileIcon} style={{ color: iconColor }} />
				<span className={classes.textContent} onClick={() => openAttachment(propsAtt)}>
					<span className={classes.fileName}>{propsAtt.fileName}</span>
					{propsAtt.size && <span className={classes.fileSize}>{formatBytes(propsAtt.size)}</span>}
				</span>
				<IconButton
					size="small"
					className={classes.closeBtn}
					aria-label="supprimer la pièce jointe"
					onClick={e => {
						e.stopPropagation();
						deleteAttachment(propsAtt.index, propsAtt.fileName);
					}}
				>
					<CloseButton className={classes.closeIcon} />
				</IconButton>
			</span>
		</Tooltip>
	);
};

export default function AttachmentsList({ attachments, edit }) {
	const [anchorEl, setAnchorEl] = React.useState(null);
	const nb = attachments.length;

	const openAttachment = async url => {
		try {
			window.open(url);
		} catch (e) {
			traceError(`openAttachment-${url} failed: ${e.message}`);
			// console.error("error", e);
		}
	};

	const handleClick = event => {
		setAnchorEl(event.currentTarget);
	};

	const handleClose = () => {
		setAnchorEl(null);
	};

	return (
		<div>
			<IconButton
				aria-controls="simple-menu"
				aria-haspopup="true"
				onClick={handleClick}
				style={{ float: "right" }}
			>
				<StyledBadge badgeContent={nb}>
					<AttachFileIcon
						style={{
							fontSize: "16px",
							color: "black"
						}}
					/>
				</StyledBadge>
			</IconButton>
			<Menu
				id="simple-menu"
				anchorEl={anchorEl}
				keepMounted
				open={Boolean(anchorEl)}
				onClose={handleClose}
			>
				{attachments.map((attachment, index) => (
					<MenuItem
						key={index}
						onClick={() => {
							if (!edit) openAttachment(attachment.url);
						}}
						className={"attachmentList"}
					>
						{edit ? (
							<Attachment
								progress={attachment.progress}
								fileName={attachment.name}
								index={index}
								attachmentId={attachment.id}
								size={attachment.size}
								mimeType={attachment.mimeType}
								path={attachment.path}
								contentType={attachment.contentType}
								url={getACleanURI(attachment.url)}
							/>
						) : (
							attachment.name
						)}
					</MenuItem>
				))}
			</Menu>
		</div>
	);
}
